import { useCallback, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { useAuthStore } from '../../store/authStore'
import { useFeature } from '../../Utils/featureFlagEngine'
import { QUERY_CONFIG } from '../../lib/react-query'
import { patchSessionApi } from '../../services/api/patchSession.api'
import type { ActivePatchSession, PatchSession } from '../../services/api/patchSession.api'
import type { ApprovalTarget } from '../../app/modules/types/chatBot'
import type { TargetResolver } from '../../_metronic/partials/chat/approvalState'

/**
 * Every `/patch-session` endpoint is exempt from BOTH the global error toast
 * and the global forbidden-redirect (`SetupAxios.tsx`), because these are
 * owner-only endpoints on an otherwise org-shared resource: any member may open
 * a colleague's chat, so a 403 here is a normal state that must render in place
 * as a read-only view rather than eject the user to the dashboard.
 *
 * The consequence is that these rejections are the caller's ONLY signal — an
 * unhandled one is complete silence. The 403 in particular is load-bearing: it
 * is what `ChatInner`'s owner gate reads to keep the read-only card off a
 * colleague's chat, so the error is typed rather than left as `Error`.
 */
export type PatchSessionError = AxiosError<{ detail?: string; message?: string }>

/**
 * Module-local on purpose. It was barrel-exported with no consumer outside this
 * file; a query-key factory nothing else reads is a surface that invites a
 * second, divergent way to reach the same cache entry. Re-export it the moment
 * something outside genuinely needs to invalidate these.
 */
const patchSessionKeys = {
  detail: (orgId: string, chatId: string | null | undefined) =>
    ['patchSession', 'detail', orgId, chatId ?? ''] as const,
  active: (orgId: string) => ['patchSession', 'active', orgId] as const,
}

/**
 * Read-only view of a chat's patch session.
 *
 * The agent now owns the whole lifecycle — starting a session, checking its
 * connectivity and ending it are all its backend tools, proposed as
 * approval-gated tool calls. There is nothing for the UI to mutate, so this hook
 * no longer carries `start` / `checkConnectivity` / `end`: it reads the session
 * and hands back `refresh`, which the caller fires when the agent's patch tools
 * settle so the read-only card reflects the new server state.
 */
export function usePatchSession(chatId: string | null | undefined) {
  const qc = useQueryClient()
  const orgId = useAuthStore((s) => s.orgId)
  const { isEnabled } = useFeature()
  const patchEnabled = isEnabled('vulnerabilities_chat_patch')
  const key = patchSessionKeys.detail(orgId, chatId)

  /**
   * The current session for this chat. 404 → null (a normal chat). Gated by the
   * Patcher flag: when off, no request fires and `session` stays null, so the
   * chat never auto-restores into patch mode.
   *
   * `staleTime` is set explicitly (to CHAT's, i.e. always-stale) rather than
   * inherited, so that a `connected` session that has lapsed is re-read on
   * remount and settles to `expired` (§6) instead of being observable solely
   * through an agent connectivity check, and so a `refresh()` after the agent
   * changes the session is never served a cached copy.
   */
  const query = useQuery<PatchSession | null, PatchSessionError>({
    queryKey: key,
    queryFn: () => patchSessionApi.get(chatId as string),
    enabled: !!chatId && patchEnabled,
    retry: false,
    staleTime: QUERY_CONFIG.CHAT.staleTime,
  })
  const session = query.data ?? null

  /**
   * Re-read the session on demand. The manual mutations used to keep the card
   * fresh by invalidating this query on success; with them gone, `ChatInner`
   * calls this when a patch tool result lands on the chat stream (the agent
   * started / checked / ended a session) so the card catches up with the server.
   *
   * Stable across renders so the effect that drives it in `ChatInner` fires on
   * the tool activity, not on the callback identity changing.
   */
  const refresh = useCallback(
    () => qc.invalidateQueries({ queryKey: key }),
    // `key` is a fresh tuple each render but its contents are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc, orgId, chatId],
  )

  return {
    session,
    sessionLoading: query.isLoading,
    refresh,
    /**
     * The READ failing is a normal state, not an edge case. `patchSessionApi.get`
     * maps only 404 to `null`, so a 403 rejects — and merely opening a
     * colleague's chat fires this request and gets one, since `/patch-session`
     * is owner-only on an org-shared resource. Without this, `session === null`
     * with `sessionLoading === false` is indistinguishable from "no session",
     * and the owner gate cannot tell a non-owner apart from a chat that simply
     * has no session yet.
     */
    sessionError: query.error,
  }
}

/**
 * Never a fresh `[]` per render. `rows` below falls back to this whenever
 * there is nothing usable yet, and `activeChatIds` / `byChatId` / `sessions`
 * are all memoized off `rows`'s identity — a literal `[]` at the fallback
 * site would recompute every one of them on every render regardless of
 * whether the listing actually changed.
 */
const EMPTY_ACTIVE_SESSIONS: ActivePatchSession[] = []

/**
 * Caller's active (pending/connected) sessions, indexed for chat-list badges
 * and, since this task, the patch panel and the approval card's target
 * resolver — both of which act on production hosts.
 *
 * Same two-part fix as `usePatchSession`'s detail query, for the same reason:
 * `staleTime` alone only corrects a remount, and `refresh` below is what
 * makes an in-place update (a session another chat's turn just started or
 * ended) show up without one. This query has no equivalent of the detail
 * query's `refresh()`-on-tool-settle caller wired up until `ChatInner` calls
 * it — see that file's settled-patch-tool effect.
 */
export function useActivePatchSessions() {
  const qc = useQueryClient()
  const orgId = useAuthStore((s) => s.orgId)
  const { isEnabled } = useFeature()
  const patchEnabled = isEnabled('vulnerabilities_chat_patch')
  const key = patchSessionKeys.active(orgId)
  const query = useQuery({
    queryKey: key,
    queryFn: patchSessionApi.listActive,
    // Gated by the Patcher flag: when off, no request fires and the chat-list
    // active-session badge never shows (activeChatIds stays empty).
    enabled: !!orgId && patchEnabled,
    staleTime: QUERY_CONFIG.CHAT.staleTime,
  })
  // Trusted no further than any other wire read on this branch: a malformed
  // body degrades to empty here rather than throwing inside the `useMemo`s
  // below, which run during `ChatInner`'s render.
  const rows: ActivePatchSession[] = Array.isArray(query.data) ? query.data : EMPTY_ACTIVE_SESSIONS
  const activeChatIds = useMemo(() => new Set(rows.map((r) => r.chat_id)), [rows])
  const byChatId = useMemo(
    () => new Map(rows.map((r) => [r.chat_id, r])),
    [rows],
  )

  /**
   * The listing scoped to what a patch panel can actually draw: live rows
   * only. The service already filters to pending/connected, but a contract
   * change on its end must not be the only thing standing between a stray
   * terminal row and the screen — so this filters again rather than trusting
   * the wire.
   */
  const sessions = useMemo(
    () =>
      rows.filter(
        (row) => row.status === 'pending_authorization' || row.status === 'connected',
      ),
    [rows],
  )

  /**
   * Resolves a tool call's `session_id` for the approval card's target line.
   *
   * Only the caller's OWN sessions are in this listing, so a session id this
   * cannot resolve is one the user may not act on anyway — `null` is the
   * honest answer, not a lookup failure to work around.
   *
   * Always the `ssh` arm: this listing is patch (SSH) sessions exclusively —
   * the `verify` arm is resolved by the service itself, off no session at
   * all, and never reaches this resolver.
   */
  const targetOf = useCallback<TargetResolver>(
    (sessionId: string): ApprovalTarget | null => {
      const found = sessions.find((row) => row.id === sessionId)
      return found
        ? { kind: 'ssh', host: found.target_host, username: found.target_username, port: found.target_port }
        : null
    },
    [sessions],
  )

  /**
   * Re-read the listing on demand. `ChatInner` calls this alongside
   * `usePatchSession(chatId)`'s own `refresh` whenever a patch tool result
   * settles: the detail query only ever holds THIS chat's row, so a session
   * started, checked or ended from another conversation never touches it —
   * this is the only thing that invalidates `active`, since react-query's key
   * prefix matching does not reach it from `detail`.
   */
  const refresh = useCallback(
    () => qc.invalidateQueries({ queryKey: key }),
    // `key` is a fresh tuple each render but its contents are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc, orgId],
  )

  return { activeChatIds, byChatId, isLoading: query.isLoading, sessions, targetOf, refresh }
}
